import { useMemo, useState } from "react";
import { stonePatternIssues, STONE_PATTERN_LIBRARY, STONE_PATTERN_MAX_LAYERS } from "../../../shared/api-contract";
import { Icon } from "../../kit";
import { useRing } from "../ringContext";
import {
  addLayer,
  canAddLayer,
  canRemoveLayer,
  convertToFreePatch,
  currentPattern,
  freshSeed,
  libraryPatch,
  patternBand,
  removeLayer,
  sendPattern,
  startFromRow,
  surprisePatch,
  thumbGeometry,
} from "../patternUi";
import type { LayerCtx } from "../patternUi";
import { LayerCard } from "./LayerCard";
import { PatternLibraryPanel } from "./PatternLibrary";
import { PatternThumb } from "./PatternThumb";

/**
 * Gems › Pattern (contract v67). A stone pattern is 1–4 layers, each a small
 * parametric description the contract's generator expands into stones. The
 * layers are the truth: a library entry, Surprise me and Start from current
 * row only fill them in, and every value stays editable afterwards.
 *
 * Every write is `applyRingDesign({ diamond: { style: "pattern" }, stonePattern })`
 * (see patternUi.ts), and every one is a normal ring edit, so the builder's
 * Undo takes each back — including a Surprise.
 */
export function PatternEditor() {
  const { state, r, apply } = useRing();
  const pattern = useMemo(() => currentPattern(state), [state]);
  const band = useMemo(
    () => patternBand(state),
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [state.widthMM, state.thicknessMM, state.spec?.innerDiameterMM, state.diamond.stoneSizeMM],
  );
  const lc: LayerCtx = useMemo(() => ({ band }), [band]);
  const geometry = useMemo(() => thumbGeometry(pattern, band), [pattern, band]);
  const issues = useMemo(() => stonePatternIssues(geometry.result, band), [geometry, band]);
  const [openLayer, setOpenLayer] = useState<number | null>(0);
  const [libraryOpen, setLibraryOpen] = useState(false);
  const libName = pattern.libraryId ? STONE_PATTERN_LIBRARY.find((e) => e.id === pattern.libraryId)?.name : undefined;
  const count = geometry.result.stones.length;

  return (
    <div className="flex flex-col gap-3" data-pattern-editor>
      <div className="flex flex-col gap-2 rounded-xl border border-ed-border bg-white p-3">
        <div className="flex items-baseline justify-between gap-2">
          <span className="font-body text-[12.5px] font-semibold text-ed-text">Stone pattern</span>
          <span className="font-body text-[11px] tabular-nums text-ed-text-2">
            {count} stone{count === 1 ? "" : "s"} · {pattern.layers.length} layer{pattern.layers.length === 1 ? "" : "s"}
          </span>
        </div>
        <div className="flex h-[76px] items-center justify-center overflow-hidden rounded-lg bg-[#f7f5f0] px-2">
          <PatternThumb pattern={pattern} band={band} geometry={geometry} highlightLayer={openLayer} className="h-full w-full" title="Your pattern, unrolled" />
        </div>
        <span className="font-body text-[11px] text-ed-text-2">
          The band unrolled flat — 12 o&rsquo;clock in the middle.{libName ? ` Started from “${libName}”.` : ""}
        </span>
        {issues.length ? (
          <ul className="flex flex-col gap-0.5" data-pattern-issues={issues.length}>
            {issues.slice(0, 3).map((x, i) => (
              <li key={i} className="font-body text-[11px] leading-snug text-amber-800">
                {pattern.layers.length > 1 ? `Layer ${x.layer + 1}: ` : ""}{x.message}
              </li>
            ))}
          </ul>
        ) : null}

        <div className="grid grid-cols-2 gap-2 pt-1">
          <ActionButton icon="grid_view" primary onClick={() => setLibraryOpen(true)}>
            Library
          </ActionButton>
          <ActionButton
            icon="casino"
            onClick={() => void apply(r.applyRingDesign(surprisePatch(freshSeed(), state)))}
            title="A new random pattern that fits this band. Undo brings the last one back."
          >
            Surprise me
          </ActionButton>
          <ActionButton
            icon="linear_scale"
            onClick={() => void apply(sendPattern(r, startFromRow(state)))}
            title="Replace layer 1 with the stone row's own count, size, spacing and position"
          >
            Start from current row
          </ActionButton>
          <ActionButton
            icon="scatter_plot"
            onClick={() => void apply(r.applyRingDesign(convertToFreePatch(pattern, state)))}
            title="Turn every stone into a free stone you can drag one by one. Recesses and lines are pattern features and are left behind."
          >
            Convert to free stones
          </ActionButton>
        </div>
      </div>

      {pattern.layers.map((_, i) => (
        <LayerCard
          key={i}
          pattern={pattern}
          index={i}
          lc={lc}
          open={openLayer === i}
          onToggle={() => setOpenLayer(openLayer === i ? null : i)}
          onRemove={canRemoveLayer(pattern) ? () => {
            setOpenLayer(null);
            void apply(sendPattern(r, removeLayer(pattern, i)));
          } : undefined}
        />
      ))}

      <button
        type="button"
        disabled={!canAddLayer(pattern)}
        onClick={() => {
          setOpenLayer(pattern.layers.length);
          void apply(sendPattern(r, addLayer(pattern, band)));
        }}
        className="flex items-center justify-center gap-1.5 rounded-xl border border-dashed border-ed-line/60 bg-white px-3 py-2.5 font-body text-[12px] font-medium text-ed-text hover:border-ed-accent disabled:cursor-not-allowed disabled:opacity-40"
      >
        <Icon name="add" size={16} />
        {canAddLayer(pattern) ? "Add a layer" : `Up to ${STONE_PATTERN_MAX_LAYERS} layers`}
      </button>

      {libraryOpen ? (
        <PatternLibraryPanel
          currentId={pattern.libraryId}
          onClose={() => setLibraryOpen(false)}
          onPick={(id) => {
            const patch = libraryPatch(id);
            if (patch) {
              setOpenLayer(0);
              void apply(r.applyRingDesign(patch));
            }
          }}
        />
      ) : null}
    </div>
  );
}

function ActionButton({
  icon,
  children,
  onClick,
  title,
  primary = false,
}: {
  icon: string;
  children: string;
  onClick: () => void;
  title?: string;
  primary?: boolean;
}) {
  return (
    <button
      type="button"
      onClick={onClick}
      title={title}
      className={`flex items-center justify-center gap-1.5 rounded-lg border px-2.5 py-2 font-body text-[12px] font-medium transition-colors ${
        primary
          ? "border-ed-text bg-ed-text text-white hover:bg-[#33332f]"
          : "border-ed-border bg-white text-ed-text hover:border-ed-line"
      }`}
    >
      <Icon name={icon} size={16} />
      {children}
    </button>
  );
}
